import { apim } from "@/constants/api"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { HYDRATE } from "next-redux-wrapper"
import { store } from "@/store"
import {
  favoritesEndPoint,
  addFavoritesEndPoint,
  createFavoritesEndPoint,
  removeFavoritesEndPoint,
} from "@/constants/index"
import { checkIsFavorite, getUserPersona } from "@/utils/helper"
import { addDataLayerFav } from "@/utils/favouritesHelper"
const initialState = {
  favorites: {},
  status: "",
  fullFavoriteData: {},
  isLoginFavouriteCallback: { isCallBack: false, callBackAssetID: "" },
}
const shoppingListName = "Favorites"

export const getFavorites = createAsyncThunk("getFavorites", async () => {
  const response = await apim.get(`${favoritesEndPoint}`)
  const { results } = response?.data ?? { results: [] }
  if (results.length > 0) {
    const getVersion = Math.max(...results.map(i => i.version))
    const favoritesData = results.filter(item => item.version === getVersion)
    const { textLineItems = [] } = favoritesData[0]
    if (textLineItems.length > 0) checkIsFavorite(textLineItems)
    if (favoritesData.length) {
      return favoritesData[0]
    }
  }
  await store.dispatch(createFavorite())

  return {}
})

export const getMyAccountFavorites = createAsyncThunk(
  "getMyAccountFavorites",
  async query => {
    const response = await apim.get(`${favoritesEndPoint}`)
    const { results } = response.data
    if (results?.length > 0) {
      const getVersion = Math.max(...results.map(i => i.version))
      const d = results.filter(i => i.version === getVersion)[0]
      if (d) {
        const { lineItems = [], textLineItems = [] } = d
        const productIds = lineItems.map(item => item.productId)
        const persona = getUserPersona()

        const fl = [
          "masterSKU_s",
          "SKUColorSwatchFilename_ss",
          "SKUColorSwatchFilename_s",
          "ProductDescriptionProductShort_s",
          "ProductBrandName_s",
          "metaTitle_s",
          "productName_s",
          "id",
          "productImages.url_s",
          "productImages.url_ss",
          "slug_s",
          "Product_Category",
          "displayPriceStartingAt_s",
          "Tile_Title",
          "ProductIsExclusive_s",
          "RegionReleaseforShipment_dt",
          "SKU.Details_ss",
          "Color.SKU.Details_ss",
          "Color.SKU.Details_s",
          "productImages.labelWithUrl_ss",
          "productImages.labelWithUrl_s",
          "variantList.sku_ss",
          "variantList.sku_s",
          "RegionBrandName_s",
          `priceList.${persona}.discountedPrice_d`,
          `priceList.${persona}.saleOffer_s`,
          `priceList.${persona}.finalPrice_d`,
          `priceList.${persona}.price_d`,
          `Color.${persona}.Details_ss`,
          "ctId_s",
          "Brand_ss",
          "Country_ss",
        ]

        const res = await apim.get(`/search/plp`, {
          params: {
            q: "*:*",
            fq: `ctId_s:("${productIds.join('","')}")`,
            fl: fl.join(","),
            collections: "kohler-india",
            profilename: "profile_kohler-india_General",
            start: 0,
            rows: 50,
          },
        })

        const productData = res.data?.response?.docs ?? []
        if (productData.length > 0) {
          productData.forEach(product => {
            const d = lineItems.find(
              item => item.productId === product["ctId_s"]
            )
            d.productData = product
          })
        }

        let sortLineItems = []
        if (lineItems.length > 0) {
          sortLineItems = lineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        let sortTextLineItems = []
        if (textLineItems.length > 0) {
          sortTextLineItems = textLineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        return {
          sortProducts: sortLineItems ?? [],
          sortMedia: sortTextLineItems ?? [],
          d,
        }
      } else {
        await store.dispatch(createFavorite())
      }
    }
    await store.dispatch(createFavorite())

    return null
  }
)

export const createFavorite = createAsyncThunk("createFavorite", async () => {
  const response = await apim.post(`${createFavoritesEndPoint}`, {
    name: { "en-IN": shoppingListName },
  })
  return response.data ?? {}
})

export const addProduct = createAsyncThunk(
  "addProduct",
  async (sku, thunkAPI) => {
    const state = thunkAPI.getState()
    let id
    let version
    let favorites
    if (
      state?.favorites.favorites !== undefined &&
      Object.keys(state?.favorites.favorites).length > 0
    ) {
      favorites = state?.favorites?.favorites
      id = favorites?.id
      version = favorites?.version
    } else {
      const favoritesMain = await store.dispatch(createFavorite())
      favorites = favoritesMain?.payload

      id = favorites?.id
      version = favorites?.version
    }

    const body = {
      version,

      actions: [
        {
          action: "addLineItem",
          sku,
          custom: {
            type: {
              key: "lineItemShoppingList",
              typeId: "type",
            },
          },
        },
      ],
    }

    const response = await apim.post(
      `${addFavoritesEndPoint(id, "product")}`,
      body
    )
    return response.data
  }
)

export const removeItems = createAsyncThunk(
  "removeItems",
  async (ids, thunkAPI) => {
    try {
      const state = thunkAPI.getState()
      const { id, version, lineItems } = state?.favorites?.favorites
      if (id) {
        const actions = ids.map(pid => {
          if (lineItems.find(el => el.id === pid)) {
            return {
              action: "removeLineItem",
              lineItemId: pid,
            }
          } else {
            return {
              action: "removeTextLineItem",
              textLineItemId: pid,
            }
          }
        })

        const response = await apim.post(`${removeFavoritesEndPoint(id)}`, {
          version,
          actions,
        })
        return response.data
      }
      return {}
    } catch (error) {
      console.error("Failed to remove from favorites", error)
    }
  }
)

export const myAccountRemoveItems = createAsyncThunk(
  "myAccountRemoveItems",
  async (ids, thunkAPI) => {
    const state = thunkAPI.getState()
    const { id, version, lineItems } = state?.favorites?.fullFavoriteData?.d
    if (id) {
      const actions = ids.map(pid => {
        if (lineItems.find(el => el.id === pid)) {
          return {
            action: "removeLineItem",
            lineItemId: pid,
          }
        } else {
          return {
            action: "removeTextLineItem",
            textLineItemId: pid,
          }
        }
      })

      const response = await apim.post(`${removeFavoritesEndPoint(id)}`, {
        version,
        actions,
      })
      if (response?.data) {
        const { lineItems = [], textLineItems = [] } = response?.data
        const productIds = lineItems.map(item => item.productId)
        const persona = getUserPersona()

        const fl = [
          "masterSKU_s",
          "SKUColorSwatchFilename_ss",
          "SKUColorSwatchFilename_s",
          "ProductDescriptionProductShort_s",
          "ProductBrandName_s",
          "metaTitle_s",
          "productName_s",
          "id",
          "productImages.url_s",
          "productImages.url_ss",
          "slug_s",
          "Product_Category",
          "displayPriceStartingAt_s",
          "Tile_Title",
          "ProductIsExclusive_s",
          "RegionReleaseforShipment_dt",
          "SKU.Details_ss",
          "Color.SKU.Details_ss",
          "Color.SKU.Details_s",
          "productImages.labelWithUrl_ss",
          "productImages.labelWithUrl_s",
          "variantList.sku_ss",
          "variantList.sku_s",
          "RegionBrandName_s",
          `priceList.${persona}.discountedPrice_d`,
          `priceList.${persona}.saleOffer_s`,
          `priceList.${persona}.finalPrice_d`,
          `priceList.${persona}.price_d`,
          `Color.${persona}.Details_ss`,
          "ctId_s",
          "Brand_ss",
          "Country_ss",
        ]

        const res = await apim.get(`/search/plp`, {
          params: {
            q: "*:*",
            fq: `ctId_s:("${productIds.join('","')}")`,
            fl: fl.join(","),
            collections: "kohler-india",
            profilename: "profile_kohler-india_General",
            start: 0,
            rows: 50,
          },
        })

        const productData = res.data?.response?.docs ?? []
        if (productData.length > 0) {
          productData.forEach(product => {
            const d = lineItems.find(
              item => item.productId === product["ctId_s"]
            )
            d.productData = product
          })
        }

        let sortLineItems = []
        if (lineItems.length > 0) {
          sortLineItems = lineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        let sortTextLineItems = []
        if (textLineItems.length > 0) {
          sortTextLineItems = textLineItems.sort(function (a, b) {
            return new Date(b.addedAt) - new Date(a.addedAt)
          })
        }
        return {
          sortProducts: sortLineItems ?? [],
          sortMedia: sortTextLineItems ?? [],
          d: response?.data ?? {},
        }
      }
    }
    return {}
  }
)

export const addMedia = createAsyncThunk(
  "addMedia",
  async (params, thunkAPI) => {
    const {
      type = "",
      name = "",
      url = "",
      description = "",
      articleType = "",
      thumbnail = "",
      needDataLayerToAdd = true,
      caption = "",
      section = "",
      videoName = "",
      videoId = "",
      errorMessage = "",
      successMsg = "",
      successCallback = () => {},
      errorCallback = () => {},
    } = params
    try {
      const state = thunkAPI.getState()
      let id
      let version
      let favorites
      if (state?.favorites?.favorites) {
        favorites = state?.favorites?.favorites
        id = favorites?.id
        version = favorites?.version
      } else {
        const favoritesMain = await store.dispatch(createFavorite())
        favorites = favoritesMain?.payload
        id = favorites?.id
        version = favorites?.version
      }
      const body = {
        version: version,
        actions: [
          {
            action: "addTextLineItem",
            name: {
              "en-IN": name,
            },
            description: {
              "en-IN": description,
            },
            custom: {
              type: {
                key: "textLineItemShoppingList",
                typeId: "type",
              },
              fields: {
                itemType: type,
                link: url,
                thumbnail,
                articleType,
              },
            },
          },
        ],
      }
      const response = await apim.post(
        `${addFavoritesEndPoint(id, type)}`,
        body
      )
      const { textLineItems = [] } = response.data
      if (textLineItems.length > 0) {
        checkIsFavorite(textLineItems)
        successCallback()
      }
      if (needDataLayerToAdd)
        addDataLayerFav(
          successMsg,
          true,
          "add",
          caption,
          section,
          videoName,
          videoId
        )

      return response.data
    } catch (error) {
      if (needDataLayerToAdd)
        addDataLayerFav(
          errorMessage,
          false,
          "add",
          caption,
          section,
          videoName,
          videoId
        )

      errorCallback()
    }
  }
)

export const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    setFavorites(state, action) {
      state.favorites = action.payload
    },
    setLoginFavouriteCallback(state, action) {
      state.isLoginFavouriteCallback = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase([HYDRATE], (state, action) => {
        return {
          ...state,
          ...action.payload.favorites,
        }
      })
      .addCase(getFavorites.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getFavorites.fulfilled, (state, action) => {
        state.favorites = action.payload

        state.status = "succeeded"
      })
      .addCase(getFavorites.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })

      .addCase(getMyAccountFavorites.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getMyAccountFavorites.fulfilled, (state, action) => {
        state.fullFavoriteData = action.payload

        state.status = "succeeded"
      })
      .addCase(getMyAccountFavorites.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })

      .addCase(myAccountRemoveItems.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(myAccountRemoveItems.fulfilled, (state, action) => {
        state.fullFavoriteData = action.payload

        state.status = "succeeded"
      })
      .addCase(myAccountRemoveItems.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })

      .addCase(addProduct.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addProduct.fulfilled, (state, action) => {
        state.favorites = action.payload

        state.status = "succeeded"
      })
      .addCase(addProduct.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(createFavorite.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(createFavorite.fulfilled, (state, action) => {
        state.favorites = action.payload
        state.status = "succeeded"
      })
      .addCase(createFavorite.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addMedia.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addMedia.fulfilled, (state, action) => {
        state.favorites = { ...state.favorites, ...action.payload }
        state.status = "succeeded"
      })
      .addCase(addMedia.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(removeItems.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(removeItems.fulfilled, (state, action) => {
        state.favorites = action.payload
        state.status = "succeeded"
      })
      .addCase(removeItems.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
  },
})

export const { setFavorites, setLoginFavouriteCallback } =
  favoritesSlice.actions
export const selectFavoritesState = state => state.favorites
export default favoritesSlice.reducer
